import http from '../../../services/api'
import type { APIPaginatedResponse, APIResponse, Filters, Pagination } from '../../../services/types'
import { getRequestParamString } from '../../../services/utils/utils'
import type { InventoryTransfer, InventoryTransferListItem, InventoryTransferStatistics } from '../types'

const apiPath = import.meta.env.VITE_API_PATH

export type TransferBatchAllocationPayload = {
  batch_number: string
  quantity: number
}

export type TransferLinePayload = {
  item_uuid: string
  quantity: number
  /** Selected transfer UoM; backend converts to base and snapshots factor. */
  unit_uuid?: string
  /** Preferred serial field (alias of `serials`). */
  serial_numbers?: string[]
  serials?: string[]
  batch_allocations?: TransferBatchAllocationPayload[]
}

export type CreateTransferPayload = {
  from_location_id: number
  to_location_id: number
  reference_number?: string | null
  notes?: string | null
  items: TransferLinePayload[]
}

export type ReceiveTransferLinePayload = {
  item_uuid: string
  qty_received: number
}

export type ReceiveTransferPayload = {
  destination_location_id?: number | null
  received_serials?: string[]
  serial_numbers?: string[]
  serials?: string[]
  items?: ReceiveTransferLinePayload[]
}

export type ListTransfersOptions = {
  /** Scopes list to transfers involving locations the user can access as source or destination. */
  direction?: 'outgoing' | 'incoming'
}

/**
 * Appends a query parameter to an existing `?…` / `&…` string from `getRequestParamString`.
 * @param paramString - Existing query string (may be empty).
 * @param param - `key=value` segment without leading `?` / `&`.
 * @returns Updated query string including the new parameter.
 */
const appendQueryParam = (paramString: string, param: string): string => {
  if (!paramString) {
    return `?${param}`
  }

  return `${paramString}&${param}`
}

/**
 * Creates and dispatches an inventory transfer (stock deducted at source).
 * @param input - Create payload aligned with `POST inventory/transfers`.
 * @returns API envelope for the created transfer detail resource.
 */
async function createTransfer(input: CreateTransferPayload) {
  return await http.post<APIResponse<InventoryTransfer>>(apiPath + 'inventory/transfers', input, {
    errorHandle: false,
  } as any)
}

/**
 * Loads a paginated transfer list with optional filters and direction scope.
 * @param filters - Optional column/search and date filters from the shared `Table` widget.
 * @param pagination - Current page and page size.
 * @param options - Extra list options (e.g. `direction=outgoing|incoming`).
 * @returns Axios response whose `data` is a Laravel paginator of list transfer resources.
 */
async function listTransfers(
  filters?: Partial<Filters>,
  pagination?: Partial<Pagination>,
  options?: ListTransfersOptions,
) {
  let paramString = getRequestParamString(filters, pagination)
  if (options?.direction) {
    paramString = appendQueryParam(paramString, `direction=${options.direction}`)
  }

  return await http.get<APIPaginatedResponse<InventoryTransferListItem>>(
    apiPath + 'inventory/transfers' + paramString,
    {
      errorHandle: false,
    } as any,
  )
}

/**
 * Loads a single transfer by UUID (includes lines and serials when present).
 * @param uuid - Transfer document UUID.
 * @returns API envelope for the transfer detail resource.
 */
async function getTransfer(uuid: string) {
  return await http.get<APIResponse<InventoryTransfer>>(apiPath + `inventory/transfers/${uuid}`, {
    errorHandle: false,
  } as any)
}

/**
 * Receives a sent transfer at the destination (full or partial).
 * @param uuid - Transfer document UUID.
 * @param input - Optional receive overrides (destination, serials, per-line qty).
 * @returns API envelope for the updated transfer detail resource.
 */
async function receiveTransfer(uuid: string, input: ReceiveTransferPayload = {}) {
  return await http.post<APIResponse<InventoryTransfer>>(apiPath + `inventory/transfers/${uuid}/receive`, input, {
    errorHandle: false,
  } as any)
}

/**
 * Loads incoming, outgoing, and pending transfer counts for the user's active location.
 * @returns API envelope for the transfer statistics resource.
 */
async function getTransferStatistics() {
  return await http.get<APIResponse<InventoryTransferStatistics>>(apiPath + 'inventory/transfers/statistics', {
    errorHandle: false,
  } as any)
}

const transfersService = {
  createTransfer,
  listTransfers,
  getTransfer,
  receiveTransfer,
  getTransferStatistics,
}

export default transfersService
